import { useState, useEffect } from 'react';
import { Settings, Loader2, AlertCircle, Save, Check, Copy } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DOC_TYPES = [
  { key: 'challan', label: 'Challan Number' },
  { key: 'po', label: 'Purchase Order Number' },
  { key: 'dn', label: 'Debit Note Number' },
  { key: 'est', label: 'Estimate / Quotation Number' },
  { key: 'pi', label: 'Proforma Invoice Number' },
  { key: 'cn', label: 'Credit Note / Return Number' },
  { key: 'payment_in', label: 'Payment In Receipt Number' },
  { key: 'payment_out', label: 'Payment Out Receipt Number' },
];

const PREFIX_FIELDS = ['invoice_prefix', ...DOC_TYPES.map(dt => `${dt.key}_prefix`)];
const SUFFIX_FIELDS = ['invoice_suffix', ...DOC_TYPES.map(dt => `${dt.key}_suffix`)];
const today = new Date();
const currentFinancialYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
const FINANCIAL_YEARS = Array.from({ length: 7 }, (_, index) => {
  const start = currentFinancialYear - 3 + index;
  return `${start}-${String(start + 1).slice(-2)}`;
});

const fmtVal = (formatted, raw) => formatted ?? (raw != null ? String(raw) : '');

const CopyAllButton = ({ onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-1 text-[10px] font-bold normal-case tracking-normal text-orange-600 hover:text-orange-700"
    title="Copy this value to this column for every document row and company">
    <Copy className="h-3 w-3" /> Copy all
  </button>
);

const FinancialYearPicker = ({ value, onChange, testId }) => (
  <select value={value || ''}
    onChange={event => event.target.value && onChange(event.target.value)} data-testid={testId}
    className="mt-1 w-full rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600 focus:border-orange-500 focus:outline-none"
    aria-label="Select financial year as suffix">
    <option value="">Select year…</option>
    {!FINANCIAL_YEARS.includes(value) && value && <option value={value}>{value} (current)</option>}
    {FINANCIAL_YEARS.map(year => <option key={year} value={year}>FY {year}</option>)}
  </select>
);

export const ConfigSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [copyMessage, setCopyMessage] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/invoice-config`, getAuthHeaders());
        setConfigs(res.data);
        const initial = {};
        res.data.forEach(c => {
          const e = {
            invoice_prefix: c.invoice_prefix || '',
            starting_number: fmtVal(c.starting_number_formatted, c.starting_number),
            invoice_suffix: c.invoice_suffix || '',
          };
          DOC_TYPES.forEach(dt => {
            e[`${dt.key}_prefix`] = c[`${dt.key}_prefix`] || '';
            e[`${dt.key}_starting_number`] = fmtVal(c[`${dt.key}_starting_number_formatted`], c[`${dt.key}_starting_number`]);
            e[`${dt.key}_suffix`] = c[`${dt.key}_suffix`] || '';
          });
          initial[c.company_id] = e;
        });
        setEdits(initial);
      } catch { setError('Failed to load config'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const updateEdit = (companyId, field, value) => {
    setEdits(prev => ({ ...prev, [companyId]: { ...prev[companyId], [field]: value } }));
    setSaved(prev => ({ ...prev, [companyId]: false }));
  };

  const copyColumnToAll = (companyId, sourceField, column) => {
    const value = edits[companyId]?.[sourceField] || '';
    const fields = column === 'prefix' ? PREFIX_FIELDS : SUFFIX_FIELDS;
    setEdits(previous => {
      const next = { ...previous };
      configs.forEach(config => {
        const companyEdit = { ...(next[config.company_id] || {}) };
        fields.forEach(field => { companyEdit[field] = value; });
        next[config.company_id] = companyEdit;
      });
      return next;
    });
    setSaved({});
    setCopyMessage(`${column === 'prefix' ? 'Prefix' : 'Suffix'} copied to every document row and company. Save each company to confirm the changes.`);
  };

  const handleSave = async (companyId) => {
    const edit = edits[companyId];
    setSaving(prev => ({ ...prev, [companyId]: true }));
    try {
      const payload = {
        invoice_prefix: edit.invoice_prefix || null,
        starting_number: (edit.starting_number ?? '') === '' ? null : String(edit.starting_number).trim(),
        invoice_suffix: edit.invoice_suffix || null,
      };
      DOC_TYPES.forEach(dt => {
        payload[`${dt.key}_prefix`] = edit[`${dt.key}_prefix`] || null;
        const sn = edit[`${dt.key}_starting_number`];
        payload[`${dt.key}_starting_number`] = (sn ?? '') === '' ? null : String(sn).trim();
        payload[`${dt.key}_suffix`] = edit[`${dt.key}_suffix`] || null;
      });
      const res = await axios.put(`${BACKEND_URL}/api/invoice-config/${companyId}`, payload, getAuthHeaders());
      setConfigs(prev => prev.map(c => c.company_id === companyId ? res.data : c));
      const newEdit = {
        invoice_prefix: res.data.invoice_prefix || '',
        starting_number: fmtVal(res.data.starting_number_formatted, res.data.starting_number),
        invoice_suffix: res.data.invoice_suffix || '',
      };
      DOC_TYPES.forEach(dt => {
        newEdit[`${dt.key}_prefix`] = res.data[`${dt.key}_prefix`] || '';
        newEdit[`${dt.key}_starting_number`] = fmtVal(res.data[`${dt.key}_starting_number_formatted`], res.data[`${dt.key}_starting_number`]);
        newEdit[`${dt.key}_suffix`] = res.data[`${dt.key}_suffix`] || '';
      });
      setEdits(prev => ({ ...prev, [companyId]: newEdit }));
      setSaved(prev => ({ ...prev, [companyId]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [companyId]: false })), 2000);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save config'); }
    finally { setSaving(prev => ({ ...prev, [companyId]: false })); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Settings className="h-7 w-7 text-orange-500" />
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Invoice & Document Settings</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 flex items-center gap-2" data-testid="error-msg">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {copyMessage && (
        <div className="mb-6 flex items-center gap-2 rounded-sm border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700" data-testid="copy-all-message">
          <Check className="h-4 w-4" /> {copyMessage}
        </div>
      )}

      {configs.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 p-8 text-center text-slate-500">
          No companies found. Add a company first to configure numbering.
        </div>
      ) : (
        <div className="space-y-8">
          {configs.map(cfg => {
            const edit = edits[cfg.company_id] || {};
            return (
              <div key={cfg.company_id} className="bg-white rounded-sm border border-slate-200 p-6" data-testid={`config-${cfg.company_id}`}>
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                  <h2 className="text-lg font-bold text-slate-900">{cfg.company_name || cfg.company_id}</h2>
                  <button onClick={() => handleSave(cfg.company_id)} disabled={saving[cfg.company_id]}
                    data-testid={`save-btn-${cfg.company_id}`}
                    className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider text-xs px-5 py-2 rounded-sm hover:bg-orange-600 disabled:opacity-50 transition-colors">
                    {saving[cfg.company_id] ? <Loader2 className="h-4 w-4 animate-spin" /> : saved[cfg.company_id] ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                    {saved[cfg.company_id] ? 'Saved' : 'Save'}
                  </button>
                </div>

                {/* Invoice Number */}
                <div className="mb-5">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Invoice Number</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="mb-1 flex items-center justify-between text-xs font-medium uppercase text-slate-500">Prefix
                        <CopyAllButton onClick={() => copyColumnToAll(cfg.company_id, 'invoice_prefix', 'prefix')} testId={`copy-all-invoice-prefix-${cfg.company_id}`} />
                      </label>
                      <input type="text" value={edit.invoice_prefix || ''} onChange={e => updateEdit(cfg.company_id, 'invoice_prefix', e.target.value)}
                        placeholder="e.g. INV/" data-testid={`inv-prefix-${cfg.company_id}`}
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase mb-1">First Number</label>
                      <input type="text" inputMode="numeric" value={edit.starting_number ?? ''}
                        onChange={e => updateEdit(cfg.company_id, 'starting_number', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="e.g. 0001" data-testid={`inv-starting-${cfg.company_id}`}
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono" />
                      <p className="text-[10px] text-slate-400 mt-1">Use "0001" for zero-padded</p>
                    </div>
                    <div>
                      <label className="mb-1 flex items-center justify-between text-xs font-medium uppercase text-slate-500">Suffix
                        <CopyAllButton onClick={() => copyColumnToAll(cfg.company_id, 'invoice_suffix', 'suffix')} testId={`copy-all-invoice-suffix-${cfg.company_id}`} />
                      </label>
                      <input type="text" value={edit.invoice_suffix || ''} onChange={e => updateEdit(cfg.company_id, 'invoice_suffix', e.target.value)}
                        placeholder="optional" data-testid={`inv-suffix-${cfg.company_id}`}
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono" />
                      <FinancialYearPicker value={edit.invoice_suffix || ''} onChange={value => updateEdit(cfg.company_id, 'invoice_suffix', value)} testId={`inv-year-${cfg.company_id}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Current Number</label>
                      <input type="text" value={cfg.last_used_number_formatted ?? (cfg.last_used_number != null ? String(cfg.last_used_number) : '')} readOnly
                        className="w-full px-3 py-2 border border-slate-100 bg-slate-50 rounded-sm text-sm text-slate-600 font-mono cursor-not-allowed" />
                    </div>
                  </div>
                  {(() => {
                    const p = cfg.number_padding || 0; const l = cfg.last_used_number; const s = cfg.starting_number;
                    let n = null; if (l != null) n = l + 1; else if (s != null) n = s;
                    if (n == null || !edit.invoice_prefix) return null;
                    const f = p > 0 ? String(n).padStart(p, '0') : String(n);
                    return <p className="text-xs text-slate-400 mt-2">Next: <span className="font-mono font-medium text-slate-600">{edit.invoice_prefix}{f}{edit.invoice_suffix || ''}</span></p>;
                  })()}
                </div>

                {/* All doc type sections */}
                {DOC_TYPES.map(dt => {
                  const dtPrefix = edit[`${dt.key}_prefix`] || '';
                  const dtSuffix = edit[`${dt.key}_suffix`] || '';
                  const dtStarting = edit[`${dt.key}_starting_number`];
                  const dtPadding = cfg[`${dt.key}_number_padding`] || 0;
                  const dtLast = cfg[`${dt.key}_last_used_number`];
                  const dtStart = cfg[`${dt.key}_starting_number`];
                  const dtLastFmt = cfg[`${dt.key}_last_used_number_formatted`];
                  return (
                    <div key={dt.key} className="mb-5 pt-4 border-t border-slate-200">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{dt.label}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="mb-1 flex items-center justify-between text-xs font-medium uppercase text-slate-500">Prefix
                            <CopyAllButton onClick={() => copyColumnToAll(cfg.company_id, `${dt.key}_prefix`, 'prefix')} testId={`copy-all-${dt.key}-prefix-${cfg.company_id}`} />
                          </label>
                          <input type="text" value={dtPrefix} onChange={e => updateEdit(cfg.company_id, `${dt.key}_prefix`, e.target.value)}
                            placeholder="e.g. DC/" data-testid={`${dt.key}-prefix-${cfg.company_id}`}
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 uppercase mb-1">First Number</label>
                          <input type="text" inputMode="numeric" value={dtStarting ?? ''}
                            onChange={e => updateEdit(cfg.company_id, `${dt.key}_starting_number`, e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="e.g. 0001" data-testid={`${dt.key}-starting-${cfg.company_id}`}
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono" />
                          <p className="text-[10px] text-slate-400 mt-1">Use "0001" for zero-padded</p>
                        </div>
                        <div>
                          <label className="mb-1 flex items-center justify-between text-xs font-medium uppercase text-slate-500">Suffix
                            <CopyAllButton onClick={() => copyColumnToAll(cfg.company_id, `${dt.key}_suffix`, 'suffix')} testId={`copy-all-${dt.key}-suffix-${cfg.company_id}`} />
                          </label>
                          <input type="text" value={dtSuffix} onChange={e => updateEdit(cfg.company_id, `${dt.key}_suffix`, e.target.value)}
                            placeholder="optional" data-testid={`${dt.key}-suffix-${cfg.company_id}`}
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono" />
                          <FinancialYearPicker value={dtSuffix} onChange={value => updateEdit(cfg.company_id, `${dt.key}_suffix`, value)} testId={`${dt.key}-year-${cfg.company_id}`} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Current Number</label>
                          <input type="text" value={dtLastFmt ?? (dtLast != null ? String(dtLast) : '')} readOnly
                            data-testid={`${dt.key}-last-${cfg.company_id}`}
                            className="w-full px-3 py-2 border border-slate-100 bg-slate-50 rounded-sm text-sm text-slate-600 font-mono cursor-not-allowed" />
                        </div>
                      </div>
                      {(() => {
                        let n = null; if (dtLast != null) n = dtLast + 1; else if (dtStart != null) n = dtStart;
                        if (n == null || !dtPrefix) return null;
                        const f = dtPadding > 0 ? String(n).padStart(dtPadding, '0') : String(n);
                        return <p className="text-xs text-slate-400 mt-2">Next: <span className="font-mono font-medium text-slate-600">{dtPrefix}{f}{dtSuffix}</span></p>;
                      })()}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
